/**
 * src/pages/HomePage.jsx
 *
 * The Adaptive Growth homepage — stage one of the funnel.
 *
 * Its job is problem recognition, not selling. A visitor should leave
 * recognising three expensive conditions (a scope that cannot move, specialists
 * working independently, deliverables produced without a visible allocation
 * decision) and click through to the mechanism.
 *
 * Copy: Site Rebranding/uploads/01-homepage-copy.md
 * Strategy: Site Rebranding/uploads/00-funnel-strategy.md, stage 1
 *
 * DELIBERATE OMISSIONS
 *
 * The copy doc's proof section asks for three case studies keyed to a
 * constraint — conversion, authority, capacity — with "the measured result"
 * for each. No measured results exist in the repository, and inventing figures
 * on a page whose whole argument is transparency is not a trade worth making.
 * The section renders real testimonials instead, and PROJECT notes flag the
 * case studies as outstanding.
 *
 * Plan prices are also absent by design. The funnel qualifies before it
 * quotes, so the cards carry points and slots and the CTA is the application.
 * The real figures live in data/growthPoints.js for the routing logic.
 */

import React from "react";
import SEO from "../components/seo/SEO";
import FunnelLayout from "../components/funnel/FunnelLayout";
import {
  Wrap,
  Section,
  SectionHeading,
  Eyebrow,
  Button,
  Card,
  Stat,
  FAQ,
} from "../components/funnel/primitives";
import { PLANS, CAPABILITIES, CATALOG_ITEM_COUNT } from "../data/growthPoints";
import { VideoWall, ReviewGrid } from "../components/funnel/Testimonials";
import useTestimonials from "../lib/useTestimonials";
import "./HomePageCreative.css";
import { applicationHref } from "../data/applicationProducts";

const CREATIVE = "/images/growth-creative/";

function IllustratedCTA({ eyebrow, title, children }) {
  return (
    <section className="btk-creative-cta btk-creative-cta--blue">
      <img src={`${CREATIVE}cta-next-move-640.webp`} srcSet={`${CREATIVE}cta-next-move-640.webp 640w, ${CREATIVE}cta-next-move-1440.webp 1440w`} sizes="100vw" alt="" loading="lazy" decoding="async" width="2048" height="683" />
      <Wrap className="btk-creative-cta-content">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className="rp-h2 mt-5 max-w-2xl text-white">{title}</h2>
        <div className="mt-8 flex flex-wrap gap-4">{children}</div>
      </Wrap>
    </section>
  );
}

const PROBLEMS = [
  {
    title: "Fixed scopes",
    body: "You keep paying for last quarter's priorities.",
  },
  {
    title: "Disconnected specialists",
    body: "Every vendor improves a piece, but nobody owns the whole path.",
  },
  {
    title: "Invisible tradeoffs",
    body: "You see invoices and deliverables, but not why one initiative deserves funding before another.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Find the constraint",
    body: "We clarify the business outcome, inspect the customer journey, and identify what is slowing growth now.",
  },
  {
    n: "02",
    title: "Rank the opportunities",
    body: "We compare potential actions by impact, confidence, speed to evidence, and point cost.",
  },
  {
    n: "03",
    title: "Deploy GrowthPoints",
    body: "Your points fund the selected strategy, creative, campaign, funnel, AI, or automation work.",
  },
  {
    n: "04",
    title: "Reallocate as you learn",
    body: "If the evidence changes before work begins, change the queue. Your budget stays predictable while your priorities remain flexible.",
  },
];

/**
 * The comparison table. Bow Tie is the last column so it reads as the
 * conclusion rather than the premise.
 */
const COMPARISON = {
  columns: ["Traditional retainer", "Unlimited subscription", "Multiple freelancers", "Bow Tie GrowthPoints"],
  rows: [
    { label: "Budget", cells: ["Fixed", "Fixed", "Variable", "Predictable"] },
    { label: "Scope", cells: ["Rigid", "Broad but throughput-limited", "Fragmented", "Flexible point allocation"] },
    { label: "Strategy", cells: ["Often separate", "Usually limited", "Client-owned", "Built into prioritization"] },
    { label: "Capabilities", cells: ["Contract-dependent", "Narrow", "Vendor-dependent", "Cross-disciplinary"] },
    { label: "Priority changes", cells: ["Change order", "Queue swap", "Re-coordinate vendors", "Reorder before work starts"] },
    { label: "Visibility", cells: ["Hours or deliverables", "Requests", "Separate invoices", "Clear task values and slots"] },
  ],
};

const FAQS = [
  {
    q: "Are GrowthPoints the same as hours?",
    a: "No. Points represent the value, strategy, complexity, effort, and production capacity associated with a deliverable — not time logged.",
  },
  {
    q: "Can we change priorities?",
    a: "Yes. Work that has not started can be reordered or swapped. Work already underway has consumed capacity and cannot simply be exchanged.",
  },
  {
    q: "Do points roll over?",
    a: "Unused points reset monthly unless rollover is included in writing.",
  },
  {
    q: "What do active job slots mean?",
    a: "Points determine how much work is included. Active slots determine how many deliverables can move forward at the same time.",
  },
  {
    q: "Can we purchase a one-off project?",
    a: "Yes. One-off blocks range from 10 to 100 points and are suited to quick wins, focused projects, campaigns, sprints, and launches.",
  },
  {
    q: "Are ad spend and third-party tools included?",
    a: "No. Advertising spend, software, subscriptions, hosting, domains, plugins, APIs, AI tools, and other external platform fees are separate unless included in writing.",
  },
  {
    q: "Do you guarantee results?",
    a: "No responsible growth partner can guarantee a specific business outcome. Bow Tie guarantees a transparent process, defined work, clear point values, and honest reporting. Results also depend on the offer, market, budget, implementation, customer response, and client participation.",
  },
];

export default function HomePage() {
  const { written, video } = useTestimonials();

  return (
    <FunnelLayout>
      <SEO
        title="Adaptive Growth Agency"
        description="Turn one flexible marketing budget into strategy, creative, funnels, content, AI, and automation with Bow Tie Kreative's GrowthPoints system."
        path="/"
      />

      {/* ---------------------------------------------------------------
          HERO — 9 columns of headline against 3 of proof. The asymmetry
          is the system's, and the empty column is composed, not leftover.
          --------------------------------------------------------------- */}
      <section className="relative overflow-hidden pb-20 pt-20 md:pb-28 md:pt-28 lg:pb-32 lg:pt-32">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ background: "var(--backlight-top)" }}
        />
        <Wrap className="relative">
          <div className="grid items-end gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Eyebrow>The adaptive growth agency</Eyebrow>
              <h1 className="rp-h1 mt-6 text-[color:var(--rp-heading)] [text-wrap:pretty]">
                Put your growth budget where it works hardest.
              </h1>
              <p className="rp-lede mt-8 max-w-2xl">
                Your priorities can change overnight. Your agency should be able to change with
                them. Bow Tie Kreative gives established businesses one flexible point-based system
                for strategy, creative, funnels, content, automation and AI — so the next dollar
                funds the next best growth move.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Button to="/apply" size="lg" data-testid="hero-apply">
                  Find your best next move
                </Button>
                <Button to="/growth-points" size="lg" variant="secondary" data-testid="hero-explore">
                  Explore GrowthPoints
                </Button>
              </div>
              <p className="rp-eyebrow mt-10">
                Transparent task values · Flexible priorities · Strategy and execution · No hourly
                billing
              </p>
            </div>

            <div className="btk-hero-visual lg:col-span-5">
              <img src={`${CREATIVE}hero-growth-sculpture-640.webp`} srcSet={`${CREATIVE}hero-growth-sculpture-640.webp 640w, ${CREATIVE}hero-growth-sculpture-960.webp 960w`} sizes="(min-width: 1024px) 600px, (min-width: 640px) 660px, 100vw" alt="" width="1536" height="1024" fetchPriority="high" />
              <div className="btk-hero-stats">
              <Stat value="13" label="Disciplines" />
              <Stat value="25–110" label="Points / month" />
              <Stat value={CATALOG_ITEM_COUNT} label="Published task values" />
              </div>
            </div>
          </div>
        </Wrap>
      </section>

      {/* --------------------------------------------------------------- */}
      <Section id="problem" alt>
        <Wrap>
          <SectionHeading
            eyebrow="The old agency model breaks when priorities move"
            title="You shouldn't have to choose between a rigid retainer and a pile of freelancers."
            lede="Traditional agencies lock your budget inside a scope. Specialists focus on their channel. Freelancers wait for you to tell them what to build. Meanwhile, the real constraint moves."
          />
          <p className="rp-reveal mt-6 max-w-3xl text-[15px] leading-relaxed text-[color:var(--rp-body)]">
            One month it is your message. The next it is your landing page, follow-up sequence,
            content engine, tracking, or automation. If your resources cannot move with the
            constraint, your marketing produces activity instead of progress.
          </p>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {PROBLEMS.map((problem, i) => (
              <Card key={problem.title} className="rp-reveal" data-reveal-delay={i * 90}>
                <h3 className="rp-h4 m-0 text-[22px] font-semibold text-[color:var(--rp-heading)]">
                  {problem.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--rp-body)]">
                  {problem.body}
                </p>
              </Card>
            ))}
          </div>
        </Wrap>
      </Section>

      {/* --------------------------------------------------------------- */}
      <Section id="category" backlight className="btk-category-creative">
        <Wrap>
          <img className="btk-offgrid-ribbon" src={`${CREATIVE}offgrid-orbital-ribbon-480.webp`} srcSet={`${CREATIVE}offgrid-orbital-ribbon-480.webp 480w, ${CREATIVE}offgrid-orbital-ribbon-800.webp 800w`} sizes="(min-width: 640px) 50vw, 70vw" alt="" width="1254" height="1254" loading="lazy" decoding="async" />
          <div className="btk-category-copy">
          <SectionHeading
            eyebrow="Introducing adaptive growth"
            title="One budget. Every capability. Change direction when the evidence changes."
            lede="GrowthPoints convert your monthly investment into visible growth capacity. Use the same points across twelve execution disciplines plus strategy, then reorder the queue as the business learns."
          />
          <p className="rp-reveal mt-6 max-w-3xl text-[15px] leading-relaxed text-[color:var(--rp-body)]">
            You do not buy random tasks. We help you identify the constraint, rank the
            opportunities, and allocate your points to the work most likely to move the business
            forward.
          </p>
          <div className="rp-reveal mt-9">
            <Button to="/growth-points" size="lg">
              See the GrowthPoints system
            </Button>
          </div>
          </div>
        </Wrap>
      </Section>

      {/* --------------------------------------------------------------- */}
      <Section id="how-it-works" alt className="btk-signal-section">
        <img className="btk-signal-texture" src={`${CREATIVE}pattern-signal-field-640.webp`} alt="" width="1254" height="1254" loading="lazy" decoding="async" />
        <Wrap>
          <SectionHeading eyebrow="How it works" title="Four moves, repeated." />
          <ol className="mt-14 grid list-none gap-8 p-0 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step, i) => (
              <li
                key={step.n}
                className="rp-reveal border-t border-[color:var(--hairline-soft)] pt-6"
                data-reveal-delay={i * 90}
              >
                <span className="rp-eyebrow">Step {step.n}</span>
                <h3 className="mt-4 text-[22px] font-semibold leading-tight text-[color:var(--rp-heading)]">
                  {step.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--rp-body)]">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
          <div className="rp-reveal mt-12">
            <Button to="/apply" size="lg">
              Apply for a Growth Allocation Call
            </Button>
          </div>
        </Wrap>
      </Section>

      {/* --------------------------------------------------------------- */}
      <section className="btk-creative-cta btk-creative-cta--dark">
        <img src={`${CREATIVE}cta-connected-system-640.webp`} srcSet={`${CREATIVE}cta-connected-system-640.webp 640w, ${CREATIVE}cta-connected-system-1440.webp 1440w`} sizes="100vw" alt="" loading="lazy" decoding="async" width="2048" height="683" />
        <Wrap className="btk-creative-cta-content">
          <Eyebrow>One connected growth team</Eyebrow>
          <h2 className="rp-h2 mt-5 max-w-xl text-[color:var(--rp-heading)]">One system. More room to move.</h2>
          <p className="mt-5 max-w-md text-[color:var(--rp-body)]">Connect strategy, creative, and technology around your next priority.</p>
          <div className="mt-8"><Button to="/growth-points" size="lg">Explore GrowthPoints</Button></div>
        </Wrap>
      </section>

      <Section id="capabilities">
        <Wrap>
          <SectionHeading
            eyebrow="One connected growth team"
            title="Stop coordinating the people who are supposed to help you grow."
            lede="Use one system across every discipline below. The work changes. The operating relationship does not."
          />
          <ul className="mt-12 grid list-none gap-x-8 gap-y-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((capability, i) => (
              <li
                key={capability}
                className="rp-reveal flex items-baseline gap-3 border-b border-[color:var(--hairline-soft)] pb-4 text-[15px] text-[color:var(--rp-body)]"
                data-reveal-delay={Math.min(i, 8) * 60}
              >
                <span aria-hidden="true" className="text-brand">
                  →
                </span>
                {capability}
              </li>
            ))}
          </ul>
        </Wrap>
      </Section>

      {/* ---------------------------------------------------------------
          COMPARISON — a real <table>. It is tabular data, and a grid of
          divs would lose the row/column relationship for screen readers.
          Scrolls inside its own container on narrow screens so the page
          body never scrolls sideways.
          --------------------------------------------------------------- */}
      <Section id="comparison" alt>
        <Wrap>
          <SectionHeading eyebrow="Where the difference shows" title="Four ways to buy marketing." />
          <div className="rp-reveal mt-12 overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-left text-sm">
              <caption className="sr-only">
                How GrowthPoints compares to a traditional retainer, an unlimited subscription and
                multiple freelancers
              </caption>
              <thead>
                <tr>
                  <th scope="col" className="w-40 pb-4 pr-6" />
                  {COMPARISON.columns.map((column, i) => (
                    <th
                      key={column}
                      scope="col"
                      className={
                        "border-b border-[color:var(--hairline-soft)] pb-4 pr-6 align-bottom text-[13px] font-semibold " +
                        (i === COMPARISON.columns.length - 1
                          ? "text-brand-400"
                          : "text-[color:var(--rp-muted)]")
                      }
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON.rows.map((row) => (
                  <tr key={row.label}>
                    <th
                      scope="row"
                      className="border-b border-[color:var(--hairline-soft)] py-4 pr-6 align-top text-[13px] font-semibold uppercase tracking-[0.12em] text-[color:var(--rp-muted)]"
                    >
                      {row.label}
                    </th>
                    {row.cells.map((cell, i) => (
                      <td
                        key={`${row.label}-${COMPARISON.columns[i]}`}
                        className={
                          "border-b border-[color:var(--hairline-soft)] py-4 pr-6 align-top " +
                          (i === row.cells.length - 1
                            ? "font-semibold text-[color:var(--rp-heading)]"
                            : "text-[color:var(--rp-body)]")
                        }
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Wrap>
      </Section>

      {/* ---------------------------------------------------------------
          PROOF — real clients, in their own words and on camera.
          --------------------------------------------------------------- */}
      <Section id="proof">
        <Wrap>
          <SectionHeading
            eyebrow="Built for real businesses"
            title="Strategy is only valuable when it becomes useful work."
            lede="Hear it from the clients rather than from us. Every video and review below is real and attributed."
          />
          <VideoWall videos={video} limit={6} className="mt-14" />
          <ReviewGrid written={written} limit={3} className="mt-16" />
          <div className="rp-reveal mt-12">
            <Button to="/results" variant="secondary" size="lg">
              See more client results
            </Button>
          </div>
        </Wrap>
      </Section>

      {/* ---------------------------------------------------------------
          PLANS PREVIEW — points and slots only. Pricing is quoted on the
          call, after the application has established what fits.
          --------------------------------------------------------------- */}
      <Section id="plans" alt>
        <Wrap>
          <SectionHeading
            eyebrow="Capacity that fits the next stage"
            title="Start with the capacity you need. Add more when the opportunity earns it."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {PLANS.map((plan, i) => (
              <Card
                key={plan.id}
                featured={plan.featured}
                className="rp-reveal flex flex-col"
                data-reveal-delay={i * 90}
              >
                {plan.featured && <span className="rp-eyebrow">Most popular</span>}
                <h3 className="mt-2 text-[22px] font-semibold text-[color:var(--rp-heading)]">
                  {plan.name}
                </h3>
                <p className="mt-4 text-[15px] font-semibold text-[color:var(--rp-heading)]">
                  {plan.points} GrowthPoints / month
                </p>
                <p className="text-[15px] text-[color:var(--rp-muted)]">
                  {plan.slots} active job slots
                </p>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-[color:var(--rp-body)]">
                  {plan.best}
                </p>
                <Button to={applicationHref(plan.id)} variant={plan.featured ? "primary" : "secondary"} className="mt-6 w-full" data-testid={`home-plan-cta-${plan.id}`}>Apply for {plan.name}</Button>
              </Card>
            ))}
          </div>
          <div className="rp-reveal mt-12">
            <Button to="/growth-points#plans" size="lg" variant="secondary">
              Compare plans and point values
            </Button>
            <Button to="/products" size="lg" variant="secondary" className="mt-4 sm:ml-4 sm:mt-0">Browse all products & services</Button>
          </div>
        </Wrap>
      </Section>

      {/* --------------------------------------------------------------- */}
      <Section id="qualify" backlight>
        <Wrap>
          <SectionHeading
            eyebrow="Your best next move"
            title="Before we recommend a plan, let's find out what growth actually needs."
            lede="Complete the short Growth Application. We will review your current constraint, urgency, internal capacity and investment range. If Bow Tie is a fit, you will be invited to schedule a Growth Allocation Call."
          />
          <div className="mt-10 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="rp-reveal text-[15px] font-semibold text-[color:var(--rp-heading)]">
                You will leave that call knowing:
              </p>
              <ul className="rp-reveal mt-4 list-none space-y-3 p-0">
                {[
                  "The constraint we recommend addressing first.",
                  "The capabilities required.",
                  "A practical first-month point allocation.",
                  "The right plan or one-off block for the work.",
                  "How progress should be measured.",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-[15px] leading-relaxed text-[color:var(--rp-body)]"
                  >
                    <span aria-hidden="true" className="text-brand">
                      →
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rp-reveal lg:col-span-5">
              <Button to="/apply" size="lg" className="w-full sm:w-auto">
                Start the Growth Application
              </Button>
              <p className="rp-eyebrow mt-5">
                Approximately 5 minutes · No obligation · Reviewed by a real person
              </p>
            </div>
          </div>
        </Wrap>
      </Section>

      {/* --------------------------------------------------------------- */}
      <Section id="faq" alt>
        <Wrap>
          <SectionHeading eyebrow="Questions" title="What people ask before applying." />
          <FAQ items={FAQS} className="rp-reveal mt-12" />
        </Wrap>
      </Section>

      {/* The one saturated moment on the page. */}
      <IllustratedCTA
        eyebrow="Ready when you are"
        title="Your priorities will change. Now your growth capacity can change with them."
      >
        <Button to="/apply" size="lg" variant="onAccent" data-testid="final-cta-apply">
          Start the Growth Application
        </Button>
        <Button
          to="/growth-points"
          size="lg"
          className="border border-white/40 bg-transparent text-white hover:bg-white/10"
        >
          Explore GrowthPoints
        </Button>
      </IllustratedCTA>
    </FunnelLayout>
  );
}
